import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  useTheme,
  Chip,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePageContext } from "../hooks/usePageContext";
import { usePageActions } from "../context/PageActionsContext";
import { HeaderButton } from "./shared";
import { lightTheme } from "../styles/theme";

/** Sizing shared by the header's action buttons (e.g. PLANNER and ADD EVENT). */
const HEADER_BUTTON_SX = {
  minWidth: "auto",
  minHeight: 44,
  fontSize: "0.9rem",
  py: 0.9,
  px: 1.8,
  boxShadow: "none",
  "@media (max-width: 480px)": {
    minHeight: 42,
    py: 0.85,
    px: 1.4,
    fontSize: "0.84rem",
  },
  "&:hover": {
    boxShadow: "none",
  },
} as const;

const MobileHeader: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pageContext = usePageContext();
  const { actions } = usePageActions();

  const userInitials = React.useMemo(() => {
    const name: string =
      (user?.fullName as string) || (user?.schoolName as string) || "User";
    return name
      .split(" ")
      .map((p: string) => p.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  }, [user?.fullName, user?.schoolName]);

  const shouldUseSimpleHeader =
    !pageContext.isPageWithHeader || pageContext.hideHeaderOnMobile;

  // If this page doesn't need the enhanced header (or hides it on mobile), show the simple header
  if (shouldUseSimpleHeader) {
    return (
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: theme.zIndex.appBar,
          backgroundColor: theme.palette.background.paper,
          borderBottom: "1px solid",
          borderColor: "grey.300",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          mb: 0, // Remove space between header bar and header
          // Mobile-specific styling
          mx: -2,
          width: "calc(100% + 32px)",
          boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              fontSize: 18,
              lineHeight: 1.2,
            }}
          >
            {user?.userType === "team"
              ? `Hi, ${user.fullName}`
              : `Welcome back${
                  user?.schoolName ? `, ${user?.schoolName}` : ""
                }`}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 0.25,
              fontSize: 12,
              opacity: 0.8,
            }}
          >
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Notifications">
            <IconButton
              size="small"
              sx={{ color: "text.secondary", p: 0.5 }}
              aria-label="Notifications"
            >
              <NotificationsNoneIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Account">
            <IconButton
              size="small"
              onClick={() => navigate("/profile")}
              aria-label="Open profile"
              sx={{ p: 0.5 }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: "primary.main",
                  fontSize: 12,
                }}
              >
                {userInitials}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  // Enhanced mobile header with page context
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.14),
        background: `radial-gradient(circle at 85% 15%, ${alpha(
          theme.palette.primary.main,
          0.12
        )} 0%, transparent 45%), linear-gradient(135deg, ${theme.palette.gradients.heroSurface[0]} 0%, ${theme.palette.gradients.heroSurface[1]} 100%)`,
        borderRadius: 0,
        padding: 2,
        marginBottom: 1.5, // Add breathing room before page content
        overflow: "hidden",
        // Mobile-specific styling
        mx: -2,
        width: "calc(100% + 32px)",
        boxShadow: "none",
        minHeight: 120, // Compact mobile height
        // Mobile-specific optimizations
        "@media (max-width: 480px)": {
          padding: 1.5,
          marginBottom: 1.25,
          minHeight: 110, // Even more compact on very small screens
        },
      }}
    >
      {/* Top row: Icon and Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 1.5,
          // Mobile-specific optimizations
          "@media (max-width: 480px)": {
            gap: 1.25,
            mb: 1.25,
          },
        }}
      >
        {/* Icon */}
        {pageContext.icon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: ["Notices", "Calendar", "Drive", "Team", "Circles", "Users", "Account"].includes(pageContext.title || "") ? "transparent" : alpha(theme.palette.primary.main, 0.12),
              borderRadius: ["Notices", "Calendar", "Drive", "Team", "Circles", "Users", "Account"].includes(pageContext.title || "") ? 0 : 1.5,
              width: 40,
              height: 40,
              flexShrink: 0,
              color: "primary.main",
              // Mobile-specific icon sizing
              "@media (max-width: 480px)": {
                width: 36,
                height: 36,
                borderRadius: ["Notices", "Calendar", "Drive", "Team", "Circles", "Users", "Account"].includes(pageContext.title || "") ? 0 : 1.25,
              },
            }}
          >
            {pageContext.icon}
          </Box>
        )}

        {/* Title and Count */}
        <Box sx={{ flex: 1, minWidth: 0, ml: { xs: 2, sm: 1.25 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
              mt: "4px",
              flexWrap: "wrap",
              // Mobile-specific optimizations
              "@media (max-width: 480px)": {
                gap: 0.75,
                mb: 0.25,
                mt: "4px",
              },
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: [
                  "Notices",
                  "Calendar",
                  "Drive",
                  "Team",
                  "Circles",
                  "Users",
                  "Account",
                ].includes(pageContext.title || "")
                  ? 400
                  : 600,
                fontSize: "20px",
                color: "text.primary",
                lineHeight: 1.2,
                // Mobile-specific typography
                "@media (max-width: 480px)": {
                  fontSize: "18px",
                  lineHeight: 1.1,
                },
              }}
            >
              {pageContext.title}
            </Typography>
            {typeof actions.count === "number" && (
              <Chip
                label={actions.count.toLocaleString()}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 24,
                  px: 0.5,
                  // Mobile-specific chip sizing
                  "@media (max-width: 480px)": {
                    height: 22,
                    fontSize: "0.7rem",
                    px: 0.25,
                  },
                }}
              />
            )}
          </Box>
          {pageContext.subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: (theme) => theme.typography.body2.fontSize,
                lineHeight: 1.3,
                fontWeight: 500,
                maxWidth: { xs: "30ch", sm: "42ch" },
                // Mobile-specific typography
                "@media (max-width: 480px)": {
                  fontSize: "0.8rem",
                  lineHeight: 1.2,
                },
              }}
            >
              {pageContext.subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Bottom row: Action Buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: "flex-end",
          width: "100%",
          mt: 0.5,
          // Mobile-specific optimizations
          "@media (max-width: 480px)": {
            gap: 0.75,
            mt: 0.25,
          },
        }}
      >
        {/* Sync Button */}
        {pageContext.showSync && (
          <Button
            variant="outlined"
            onClick={() => {
              if (actions.onSync) {
                actions.onSync();
              } else {
                window.location.reload();
              }
            }}
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.35),
              color: "primary.main",
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              fontWeight: 500,
              py: 0.9,
              px: 1.5,
              borderRadius: 1,
              textTransform: "none",
              minWidth: 74,
              minHeight: 42,
              textShadow: "none",
              fontSize: "0.82rem",
              transition: "all 0.3s ease",
              // Mobile-specific button sizing
              "@media (max-width: 480px)": {
                py: 0.8,
                px: 1.25,
                minWidth: 68,
                minHeight: 40,
                fontSize: "0.8rem",
              },
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                transform: "translateY(-1px)",
              },
            }}
          >
            SYNC
          </Button>
        )}

        {((pageContext.buttonText && actions.onMainAction) ||
          actions.secondaryAction ||
          actions.onVideoPlay) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              flexShrink: 0,
            }}
          >
            {actions.secondaryAction && (
              <HeaderButton
                onClick={actions.secondaryAction.onClick}
                sx={{ ...HEADER_BUTTON_SX, mr: 0.75 }}
              >
                {actions.secondaryAction.label}
              </HeaderButton>
            )}
            {pageContext.buttonText && actions.onMainAction && (
              <HeaderButton
                onClick={() => actions.onMainAction?.()}
                sx={HEADER_BUTTON_SX}
              >
                {pageContext.buttonText}
              </HeaderButton>
            )}

            {actions.onVideoPlay && (
              <Tooltip title="Watch tutorial">
                <IconButton
                  onClick={actions.onVideoPlay}
                  aria-label="Watch tutorial"
                  sx={{
                    color: "primary.main",
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                    "@media (max-width: 480px)": {
                      width: 38,
                      height: 38,
                    },
                  }}
                >
                  <PlayCircleIcon sx={{ fontSize: 30 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MobileHeader;